import React from 'react';
import QueryHelpPageLayout from '../QueryHelpPageLayout';
import {QUERY_HELP_PAGE_INTROS} from '../content/pages';
import {SIMPLE_SAMPLES} from '../content/simpleSamples';
import QueryCopyButton from '../components/QueryCopyButton';
import {QuerySampleItem} from '../content/types';

type SimpleExampleIcon = 'search' | 'and' | 'or' | 'plus' | 'minus' | 'quote' | 'field';

const SIMPLE_EXAMPLE_ICON_BY_ID: Record<string, SimpleExampleIcon> = {
    everything: 'search',
    'single-keyword': 'search',
    'implicit-or': 'or',
    'multi-and': 'and',
    'required-plus': 'plus',
    'multi-exclude': 'minus',
    'not-operator': 'minus',
    or: 'or',
    phrase: 'quote',
    'field-text': 'field',
};

const SIMPLE_EXAMPLE_TONE_BY_ICON: Record<SimpleExampleIcon, string> = {
    search: '#12A150',
    and: '#8D5CF6',
    or: '#D98200',
    plus: '#12A150',
    minus: '#D84D7A',
    quote: '#009EF8',
    field: '#2F80ED',
};

const SimpleExampleIcon = React.memo<{icon: SimpleExampleIcon}>(({icon}) => {
    if (icon === 'and' || icon === 'or') {
        return <span className="query-help-modal__simple-op-badge">{icon.toUpperCase()}</span>;
    }

    if (icon === 'search') {
        return (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="6" />
                <line x1="20" y1="20" x2="15.5" y2="15.5" />
            </svg>
        );
    }

    if (icon === 'plus') {
        return (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="6" x2="12" y2="18" />
                <line x1="6" y1="12" x2="18" y2="12" />
            </svg>
        );
    }

    if (icon === 'minus') {
        return (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="6" y1="12" x2="18" y2="12" />
            </svg>
        );
    }

    if (icon === 'quote') {
        return (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                <path d="M7.4 7C5.9 7 5 8.2 5 9.8c0 1.5 1 2.6 2.4 2.6.2 0 .3 0 .5-.1-.2 1.2-1 2-2.1 2.3-.3.1-.5.4-.4.7.1.3.4.5.7.4C7.7 18 9.5 16 9.5 13.2 9.5 8.2 9.5 7 7.4 7Zm7 0C12.9 7 12 8.2 12 9.8c0 1.5 1 2.6 2.4 2.6.2 0 .3 0 .5-.1-.2 1.2-1 2-2.1 2.3-.3.1-.5.4-.4.7.1.3.4.5.7.4 1.6-.4 3.4-2.4 3.4-5.2C16.5 8.2 16.5 7 14.4 7Z" />
            </svg>
        );
    }

    return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <rect x="4" y="6" width="16" height="12" rx="2" />
            <line x1="8" y1="12" x2="12.5" y2="12" />
            <line x1="10.25" y1="9.5" x2="10.25" y2="14.5" />
        </svg>
    );
});

const SimpleExampleCard = React.memo<{item: QuerySampleItem}>(({item}) => {
    const icon = SIMPLE_EXAMPLE_ICON_BY_ID[item.id] || 'search';
    const tone = SIMPLE_EXAMPLE_TONE_BY_ICON[icon];

    return (
        <article className="query-help-modal__simple-card">
            <div className="query-help-modal__simple-card-header">
                <span className="query-help-modal__simple-icon" style={{color: tone}} aria-hidden="true">
                    <SimpleExampleIcon icon={icon} />
                </span>
                <div className="query-help-modal__simple-card-copy">
                    <h4 className="query-help-modal__simple-card-title">{item.title}</h4>
                    {item.description ? (
                        <p className="query-help-modal__simple-card-description">{item.description}</p>
                    ) : null}
                </div>
            </div>
            <div className="query-help-modal__simple-query-row">
                <pre className="query-help-modal__query-block query-help-modal__query-block--simple">{item.query}</pre>
                <QueryCopyButton query={item.query} />
            </div>
        </article>
    );
});

const SimpleSamplesPage = React.memo(() => (
    <QueryHelpPageLayout
        title="Simple examples"
        intro={QUERY_HELP_PAGE_INTROS.simple}
    >
        <div className="query-help-modal__sample-grid">
            {SIMPLE_SAMPLES.map((item) => (
                <SimpleExampleCard key={item.id} item={item} />
            ))}
        </div>
    </QueryHelpPageLayout>
));

export default SimpleSamplesPage;
